import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';
import './landingNew.css';

// Assets
import humanaizeLogo from './images/footerlogo.webp';
import jobAvatar from './images/Jesse_avatar.webp'; // Keep avatar, rebrand as "Job"

/* ── Animation helpers ── */
const revealParent = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1, y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], when: 'beforeChildren', staggerChildren: 0.1 },
  },
};
const revealItem = {
  hidden: { opacity: 0, y: 24, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const floatUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

/* ── FAQ Card ── */
function FAQCard({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      className="lp2-faq-card"
      layout
      initial={false}
      aria-expanded={open}
      role="button"
      tabIndex={0}
      onClick={() => setOpen(!open)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen(!open)}
    >
      <div className="lp2-faq-head">
        <h3 className="lp2-faq-q">{q}</h3>
        <div className="lp2-faq-chevron">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" fill="currentColor" />
          </svg>
        </div>
      </div>
      <motion.div
        className="lp2-faq-body"
        layout
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: open ? 1 : 0, height: open ? 'auto' : 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <p>{a}</p>
      </motion.div>
    </motion.div>
  );
}

/* ── Privacy Modal ── */
function PrivacyModal({ isVisible, onClose }) {
  if (!isVisible) return null;
  return (
    <div className="lp2-modal-overlay" onClick={onClose}>
      <div className="lp2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lp2-modal-header">
          <h2>Privacy</h2>
          <button className="lp2-modal-close" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="lp2-modal-body">
          <h3>Wat we verzamelen</h3>
          <p>HAI Job verzamelt minimale informatie:</p>
          <ul>
            <li>Account informatie (naam, email) voor authenticatie</li>
            <li>Technische gegevens zoals IP-adres en browser type voor beveiliging</li>
          </ul>
          <h3>Je gesprekken blijven priv&eacute;</h3>
          <ul>
            <li><strong>Gesprekken worden NIET opgeslagen</strong> — na je sessie worden ze verwijderd</li>
            <li><strong>Geen AI-training</strong> — je data wordt niet gebruikt om modellen te trainen</li>
            <li>Alle communicatie is versleuteld met HTTPS/TLS</li>
            <li>We delen je data nooit met derden</li>
          </ul>
          <h3>Je rechten (AVG)</h3>
          <ul>
            <li>Recht op inzage, correctie en verwijdering van je gegevens</li>
            <li>Gesprekken worden automatisch verwijderd na je sessie</li>
          </ul>
          <h3>Contact</h3>
          <p>
            Email: <a href="mailto:info@humanaize.nl">info@humanaize.nl</a> &bull;
            Website: <a href="https://humanaize.nl" target="_blank" rel="noopener noreferrer">humanaize.nl</a>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Terms Modal ── */
function TermsModal({ isVisible, onClose }) {
  if (!isVisible) return null;
  return (
    <div className="lp2-modal-overlay" onClick={onClose}>
      <div className="lp2-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lp2-modal-header">
          <h2>Voorwaarden</h2>
          <button className="lp2-modal-close" onClick={onClose}>
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="lp2-modal-body">
          <h3>Dienstverlening</h3>
          <p>HAI Job is een AI-aangedreven tool voor het schrijven van vacatureteksten:</p>
          <ul>
            <li>We leveren de dienst naar beste vermogen</li>
            <li>De gegenereerde content is een hulpmiddel, geen garantie</li>
            <li>Je bent zelf verantwoordelijk voor de eindversie die je publiceert</li>
            <li>Je gesprekken worden niet opgeslagen na je sessie</li>
          </ul>
          <h3>Abonnement en betaling</h3>
          <ul>
            <li>Abonnementen worden automatisch verlengd</li>
            <li>Je kunt op elk moment opzeggen zonder opzegtermijn</li>
            <li>Restitutie is mogelijk binnen 14 dagen na aankoop</li>
          </ul>
          <h3>Intellectueel eigendom</h3>
          <ul>
            <li>De content die Job genereert is jouw eigendom</li>
            <li>De software en AI-technologie blijven eigendom van HumanAIze</li>
          </ul>
          <h3>Contact</h3>
          <p>
            Email: <a href="mailto:info@humanaize.nl">info@humanaize.nl</a> &bull;
            Website: <a href="https://humanaize.nl" target="_blank" rel="noopener noreferrer">humanaize.nl</a>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Screenshot Marquee ── */
function ScreenshotMarquee() {
  const screens = [
    { icon: 'chat', label: 'Chat met Job', color: '#0d9488' },
    { icon: 'description', label: 'Vacaturetekst review', color: '#14b8a6' },
    { icon: 'cloud_upload', label: 'Upload bestaande tekst', color: '#0f766e' },
    { icon: 'picture_as_pdf', label: 'Professionele PDF export', color: '#10b981' },
    { icon: 'auto_fix_high', label: 'AI verfijning via chat', color: '#10b981' },
    { icon: 'analytics', label: 'Resultaat in seconden', color: '#f59e0b' },
  ];
  const items = [...screens, ...screens];
  return (
    <div className="lp2-section-full lp2-section-full--alt">
      <div className="lp2-marquee-wrap">
        <div className="lp2-marquee">
          <div className="lp2-marquee-track">
            {items.map((s, i) => (
              <div key={i} className="lp2-marquee-item" style={{ '--card-accent': s.color }}>
                <span className="material-icons">{s.icon}</span>
                <span className="lp2-marquee-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Mock Chat (hero browser content) ── */
function MockChatInterface() {
  return (
    <div className="lp2-mock-chat">
      <div className="lp2-mock-msg lp2-mock-msg--bot">
        <div className="lp2-mock-msg--bot-header">
          <img src={jobAvatar} alt="" />
          <span>HAI Job</span>
        </div>
        Hoi! Ik ben Job, jouw AI recruiter. Vertel me over de functie die je wilt invullen, dan schrijf ik een pakkende vacaturetekst voor je.
      </div>
      <div className="lp2-mock-msg lp2-mock-msg--user">
        We zoeken een ervaren frontend developer voor ons team in Amsterdam...
      </div>
      <div className="lp2-mock-msg lp2-mock-msg--bot">
        <div className="lp2-mock-msg--bot-header">
          <img src={jobAvatar} alt="" />
          <span>HAI Job</span>
        </div>
        Top! Ik ga hier een sterke vacaturetekst van maken. Nog een paar vragen: wat voor tech stack gebruiken jullie?
      </div>
      <div className="lp2-mock-msg lp2-mock-msg--bot" style={{ maxWidth: '120px' }}>
        <div className="lp2-typing-dots">
          <span /><span /><span />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   LANDING PAGE — Main Component
   ═══════════════════════════════════════════════════════════════════════════ */

export default function LandingNew() {
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [plan, setPlan] = useState('personal');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Refs for scroll-reveal
  const featRef = useRef(null);
  const featInView = useInView(featRef, { margin: '-10% 0px -10% 0px', once: true });

  /* Handle referer param for trial ending redirect */
  useEffect(() => {
    const referer = searchParams.get('referer');
    if (referer === 'free_trial_ending') {
      if (isAuthenticated) {
        navigate('/account');
      } else {
        sessionStorage.setItem('post_login_redirect', '/account');
        loginWithRedirect({ appState: { returnTo: '/account' } });
      }
    }
  }, [searchParams, isAuthenticated, navigate, loginWithRedirect]);

  /* Meta tags */
  useEffect(() => {
    document.title = 'HAI Job — Schrijf vacatureteksten die kandidaten aanspreken';
    const meta = document.querySelector('meta[name="description"]') || document.createElement('meta');
    meta.name = 'description';
    meta.content = 'HAI Job: jouw AI recruiter. Schrijf in minuten professionele vacatureteksten die kandidaten echt aanspreken. Probeer 7 dagen gratis.';
    if (!meta.parentNode) document.head.appendChild(meta);
  }, []);

  /* Mailto for enterprise */
  const mailtoHref = useMemo(() => {
    const to = encodeURIComponent('mano.radema@humanaize.nl, info@jessegeul.nl');
    const subject = encodeURIComponent(
      `Interesse: HAI Job – ${plan === 'personal' ? 'Personal' : 'Enterprise'}`
    );
    const planLabel = plan === 'personal' ? 'Personal (\u20AC49 per maand)' : 'Enterprise (maatwerk offerte)';
    const body = encodeURIComponent(
      `Hi HAI Job team,\n\nIk wil graag een proefaccount en stel het abonnement alvast in op:\nAbonnement: ${planLabel}\n\nNaam: ${form.name || '-'}\nE-mail: ${form.email || '-'}\nTelefoon: ${form.phone || '-'}\n\nGroet!`
    );
    return `mailto:${to}?subject=${subject}&body=${body}`;
  }, [form, plan]);

  /* Stripe checkout */
  const handleAnonymousCheckout = async (e) => {
    if (e) e.preventDefault();
    if (!form.email || !form.email.includes('@')) {
      setCheckoutError('Voer een geldig email adres in');
      return;
    }
    if (!form.name || form.name.trim().length < 2) {
      setCheckoutError('Voer je volledige naam in');
      return;
    }
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const response = await fetch(`${API_URL}/api/checkout/anonymous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, name: form.name, plan: 'personal' }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutError(error.message || 'Er is iets misgegaan. Probeer het opnieuw.');
      setCheckoutLoading(false);
    }
  };

  return (
    <main className="landing-new">

      {/* ═══════ 1. NAVIGATION ═══════ */}
      <nav className="lp2-nav">
        <div className="lp2-nav-inner">
          <div className="lp2-nav-brand">
            <img className="lp2-nav-logo" src={jobAvatar} alt="" />
            <span className="lp2-nav-name">HAI Job</span>
          </div>
          <div className="lp2-nav-links">
            <a href="#hoe-het-werkt" className="lp2-nav-link">Hoe het werkt</a>
            <a href="#features" className="lp2-nav-link">Features</a>
            <a href="#over-job" className="lp2-nav-link">Over Job</a>
            <a href="#prijzen" className="lp2-nav-link">Prijzen</a>
          </div>
          <div className="lp2-nav-actions">
            {!isAuthenticated ? (
              <>
                <button
                  className="lp2-nav-login"
                  onClick={() => loginWithRedirect({ appState: { returnTo: '/app' } })}
                >
                  Inloggen
                </button>
                <button
                  className="lp2-nav-cta"
                  onClick={() => document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Probeer gratis
                </button>
              </>
            ) : (
              <Link className="lp2-nav-cta" to="/app">
                Open Chat
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ═══════ 2. HERO ═══════ */}
      <header className="lp2-hero">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          <div className="lp2-badge">
            <span className="material-icons" style={{ fontSize: 14 }}>auto_awesome</span>
            AI-Powered Recruitment
          </div>
          <h1>Vacatureteksten die kandidaten aanspreken</h1>
          <p className="lp2-hero-sub">
            Schrijf in minuten pakkende vacatureteksten met HAI Job. Praat, typ of upload
            — Job maakt er een professionele tekst van waar kandidaten op reageren.
          </p>
          <div className="lp2-hero-actions">
            {!isAuthenticated ? (
              <>
                <button
                  className="lp2-btn-primary"
                  onClick={() => loginWithRedirect({ appState: { returnTo: '/app' } })}
                >
                  <span className="material-icons">chat</span>
                  Start gratis
                </button>
                <button
                  className="lp2-btn-ghost"
                  onClick={() => document.getElementById('hoe-het-werkt')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Bekijk hoe het werkt
                </button>
              </>
            ) : (
              <Link className="lp2-btn-primary" to="/app" style={{ textDecoration: 'none' }}>
                <span className="material-icons">chat</span>
                Open de chat
              </Link>
            )}
          </div>
        </motion.div>

        {/* Browser mockup with mock chat */}
        <motion.div
          className="lp2-browser"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
        >
          <div className="lp2-browser-bar">
            <div className="lp2-browser-dot" />
            <div className="lp2-browser-dot" />
            <div className="lp2-browser-dot" />
            <span className="lp2-browser-url">job.humanaize.nl</span>
          </div>
          <MockChatInterface />
        </motion.div>
      </header>

      {/* ═══════ 3. STATS ═══════ */}
      <motion.section
        className="lp2-stats"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="lp2-stats-card">
          <div className="lp2-stat">
            <span className="material-icons lp2-stat-icon">description</span>
            <span className="lp2-stat-number">500+</span>
            <span className="lp2-stat-label">Vacatures geschreven</span>
          </div>
          <div className="lp2-stat">
            <span className="material-icons lp2-stat-icon">schedule</span>
            <span className="lp2-stat-number">10 min</span>
            <span className="lp2-stat-label">Per vacaturetekst</span>
          </div>
          <div className="lp2-stat">
            <span className="material-icons lp2-stat-icon">verified</span>
            <span className="lp2-stat-number">24/7</span>
            <span className="lp2-stat-label">Altijd beschikbaar</span>
          </div>
        </div>
      </motion.section>

      {/* ═══════ 4. HOW IT WORKS ═══════ */}
      <div className="lp2-section-full lp2-section-full--alt">
        <section id="hoe-het-werkt" className="lp2-section lp2-section--center" style={{ padding: '80px 24px' }}>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-10%' }}
            variants={revealParent}
          >
            <motion.div variants={revealItem}>
              <span className="lp2-section-label">
                <span className="material-icons" style={{ fontSize: 14 }}>route</span>
                Zo werkt het
              </span>
              <h2 className="lp2-section-title">In 3 stappen naar een sterke vacaturetekst</h2>
              <p className="lp2-section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                Van een kort gesprek naar een professionele, publicatie-klare vacaturetekst.
              </p>
            </motion.div>

            <div className="lp2-steps">
              <motion.div className="lp2-step" variants={revealItem} whileHover={{ y: -6 }}>
                <div className="lp2-step-number">1</div>
                <span className="material-icons lp2-step-icon">chat</span>
                <h3>Beschrijf je functie</h3>
                <p>Vertel Job over de functie, het team en wat je zoekt. Typen of inspreken, allebei kan.</p>
              </motion.div>

              <motion.div className="lp2-step" variants={revealItem} whileHover={{ y: -6 }}>
                <div className="lp2-step-number">2</div>
                <span className="material-icons lp2-step-icon">auto_awesome</span>
                <h3>Job schrijft de vacature</h3>
                <p>Op basis van je input schrijft Job een complete, pakkende vacaturetekst die kandidaten aanspreekt.</p>
              </motion.div>

              <motion.div className="lp2-step" variants={revealItem} whileHover={{ y: -6 }}>
                <div className="lp2-step-number">3</div>
                <span className="material-icons lp2-step-icon">tune</span>
                <h3>Verfijn en publiceer</h3>
                <p>Pas de tekst aan via de chat, download als PDF of kopieer direct naar je ATS.</p>
              </motion.div>
            </div>
          </motion.div>
        </section>
      </div>

      {/* ═══════ 5. FEATURES SHOWCASE ═══════ */}
      <section id="features" className="lp2-section" ref={featRef}>
        <motion.div
          initial="hidden"
          animate={featInView ? 'show' : 'hidden'}
          variants={revealParent}
        >
          <motion.div variants={revealItem}>
            <span className="lp2-section-label">
              <span className="material-icons" style={{ fontSize: 14 }}>star</span>
              Features
            </span>
            <h2 className="lp2-section-title">Alles wat je nodig hebt</h2>
            <p className="lp2-section-sub">Krachtige AI-tools die vacatureteksten schrijven die het verschil maken.</p>
          </motion.div>
        </motion.div>

        {/* Row 1: Mock chat left, text right */}
        <motion.div
          className="lp2-feature-row"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-10%' }}
          variants={revealParent}
        >
          <motion.div className="lp2-feature-media" variants={revealItem}>
            <div className="lp2-feature-mock-chat">
              <div className="lp2-mock-msg lp2-mock-msg--bot">
                <div className="lp2-mock-msg--bot-header">
                  <img src={jobAvatar} alt="" />
                  <span>HAI Job</span>
                </div>
                Ik heb een sterke vacaturetekst geschreven voor de rol van Marketing Manager. De toon is direct en persoonlijk.
              </div>
              <div className="lp2-mock-msg lp2-mock-msg--user">
                Kun je de requirements wat realistischer maken?
              </div>
              <div className="lp2-mock-msg lp2-mock-msg--bot">
                <div className="lp2-mock-msg--bot-header">
                  <img src={jobAvatar} alt="" />
                  <span>HAI Job</span>
                </div>
                Natuurlijk! Ik heb de eisen aangepast. In plaats van "5+ jaar ervaring" staat er nu "Een paar jaar ervaring in marketing, bij voorkeur in B2B."
              </div>
            </div>
          </motion.div>
          <motion.div className="lp2-feature-text" variants={revealItem}>
            <span className="lp2-section-label">AI-Schrijfstijl</span>
            <h3>Vacatures die kandidaten aanspreken</h3>
            <p>Geen saaie opsommingen of corporate blabla. Job schrijft pakkende, menselijke teksten die kandidaten echt in beweging krijgen.</p>
            <ul className="lp2-feature-bullets">
              <li><span className="material-icons">check_circle</span>Directe, menselijke schrijfstijl</li>
              <li><span className="material-icons">check_circle</span>Vakjargon? Job begrijpt het</li>
              <li><span className="material-icons">check_circle</span>Van technisch tot creatief, elke branche</li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Row 2: text left, voice mockup right */}
        <motion.div
          className="lp2-feature-row lp2-feature-row--reverse"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-10%' }}
          variants={revealParent}
        >
          <motion.div className="lp2-feature-text" variants={revealItem}>
            <span className="lp2-section-label">Spraak naar tekst</span>
            <h3>Van belletje naar tekst in 10 minuten</h3>
            <p>Geen uren schrijven. Start een gesprek met Job, vertel over de functie, en je volledige vacaturetekst staat er binnen een paar minuten.</p>
            <ul className="lp2-feature-bullets">
              <li><span className="material-icons">check_circle</span>Praat gewoon, Job luistert en schrijft</li>
              <li><span className="material-icons">check_circle</span>Interactief: Job stelt slimme vervolgvragen</li>
              <li><span className="material-icons">check_circle</span>Resultaat direct kopieerbaar en deelbaar</li>
            </ul>
          </motion.div>
          <motion.div className="lp2-feature-media" variants={revealItem}>
            <div className="lp2-mock-voice">
              <div className="lp2-mock-mic">
                <span className="material-icons">mic</span>
              </div>
              <div className="lp2-mock-waveform">
                {[...Array(24)].map((_, i) => (
                  <div key={i} className="lp2-wave-bar" style={{ '--i': i }} />
                ))}
              </div>
              <span className="lp2-mock-voice-label">Job luistert mee en schrijft je vacature...</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Row 3: decorative flow left, text right */}
        <motion.div
          className="lp2-feature-row"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-10%' }}
          variants={revealParent}
        >
          <motion.div className="lp2-feature-media lp2-feature-media--decorative" variants={revealItem}>
            <div className="lp2-decorative-flow">
              <div className="lp2-decorative-step">
                <span className="material-icons">upload_file</span>
                <span>Upload</span>
              </div>
              <div className="lp2-decorative-arrow">
                <span className="material-icons">trending_flat</span>
              </div>
              <div className="lp2-decorative-step">
                <span className="material-icons">auto_fix_high</span>
                <span>Verbeter</span>
              </div>
              <div className="lp2-decorative-arrow">
                <span className="material-icons">trending_flat</span>
              </div>
              <div className="lp2-decorative-step lp2-decorative-step--done">
                <span className="material-icons">check_circle</span>
                <span>Klaar!</span>
              </div>
            </div>
          </motion.div>
          <motion.div className="lp2-feature-text" variants={revealItem}>
            <span className="lp2-section-label">Tekst verbeteren</span>
            <h3>Verbeter je bestaande teksten</h3>
            <p>Heb je al een vacaturetekst? Upload hem en Job optimaliseert de structuur, toon en aantrekkingskracht. Daarna kun je in de chat verder verfijnen.</p>
            <ul className="lp2-feature-bullets">
              <li><span className="material-icons">check_circle</span>Upload PDF, Word of plak je tekst</li>
              <li><span className="material-icons">check_circle</span>Concrete verbetervoorstellen, niet vaag</li>
              <li><span className="material-icons">check_circle</span>Verfijn verder via de chatfunctie</li>
            </ul>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════ 6. TESTIMONIALS ═══════ */}
      <div className="lp2-section-full lp2-section-full--alt">
        <section className="lp2-section lp2-section--center" style={{ padding: '80px 24px' }}>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-10%' }}
            variants={revealParent}
          >
            <motion.div variants={revealItem}>
              <span className="lp2-section-label">
                <span className="material-icons" style={{ fontSize: 14 }}>format_quote</span>
                Ervaringen
              </span>
              <h2 className="lp2-section-title">Wat gebruikers zeggen</h2>
              <p className="lp2-section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                Recruiters en HR-professionals over hun ervaring met HAI Job.
              </p>
            </motion.div>

            <div className="lp2-testimonials">
              <motion.div className="lp2-testimonial" variants={revealItem} whileHover={{ y: -4 }}>
                <div className="lp2-testimonial-stars">
                  {[...Array(5)].map((_, i) => <span key={i} className="material-icons">star</span>)}
                </div>
                <p className="lp2-testimonial-quote">
                  Ik had binnen 10 minuten een vacaturetekst die beter was dan alles wat ik zelf in uren had geschreven. Job begrijpt precies de juiste toon.
                </p>
                <div className="lp2-testimonial-author">
                  <div className="lp2-testimonial-avatar">MV</div>
                  <div className="lp2-testimonial-info">
                    <strong>Martijn V.</strong>
                    <span>HR Manager, Tech Bedrijf</span>
                  </div>
                </div>
              </motion.div>

              <motion.div className="lp2-testimonial" variants={revealItem} whileHover={{ y: -4 }}>
                <div className="lp2-testimonial-stars">
                  {[...Array(5)].map((_, i) => <span key={i} className="material-icons">star</span>)}
                </div>
                <p className="lp2-testimonial-quote">
                  De spraakfunctie is briljant. Ik praat gewoon over de functie en Job maakt er een sterke tekst van. Scheelt me uren per week.
                </p>
                <div className="lp2-testimonial-author">
                  <div className="lp2-testimonial-avatar">SB</div>
                  <div className="lp2-testimonial-info">
                    <strong>Sandra B.</strong>
                    <span>Corporate Recruiter</span>
                  </div>
                </div>
              </motion.div>

              <motion.div className="lp2-testimonial" variants={revealItem} whileHover={{ y: -4 }}>
                <div className="lp2-testimonial-stars">
                  {[...Array(5)].map((_, i) => <span key={i} className="material-icons">star</span>)}
                </div>
                <p className="lp2-testimonial-quote">
                  Eindelijk een tool die vacatureteksten schrijft zoals een mens ze zou schrijven. Geen corporate-blabla, maar echte teksten die aanspreken.
                </p>
                <div className="lp2-testimonial-author">
                  <div className="lp2-testimonial-avatar">RK</div>
                  <div className="lp2-testimonial-info">
                    <strong>Rick K.</strong>
                    <span>Eigenaar, Recruitmentbureau</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </section>
      </div>

      {/* ═══════ 7. ABOUT HAI JOB ═══════ */}
      <section id="over-job" className="lp2-section">
        <motion.div
          className="lp2-about"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-5%' }}
          variants={revealParent}
        >
          <motion.div className="lp2-about-story" variants={revealItem}>
            <span className="lp2-section-label">Over HAI Job</span>
            <h2>Waarom we Job bouwden</h2>
            <p className="lp2-about-lead">
              De meeste vacatureteksten zijn saai, generiek en spreken niemand aan. Dat kan beter.
            </p>
            <p>
              Bij HumanAIze geloven we dat een goede vacaturetekst het verschil maakt tussen geen
              reacties en een inbox vol met sterke kandidaten. Daarom bouwden we Job: een AI die weet
              hoe je teksten schrijft die kandidaten echt in beweging krijgen.
            </p>
            <p>
              Getraind op duizenden succesvolle vacatures en de methodiek van Nederland's beste
              recruiters. Het resultaat? Een digitale recruitment-expert die altijd beschikbaar is
              en teksten aflevert waar je trots op kunt zijn.
            </p>
            <div className="lp2-about-meta">
              <img src={humanaizeLogo} alt="HumanAIze" />
              <span>Een product van HumanAIze</span>
            </div>
          </motion.div>

          <motion.div className="lp2-about-visual" variants={revealItem}>
            <div className="lp2-about-avatar-wrap">
              <img src={jobAvatar} alt="HAI Job" className="lp2-about-avatar" />
              <div className="lp2-about-ring" />
            </div>
            <div className="lp2-about-features">
              <div className="lp2-about-feat">
                <span className="material-icons">auto_awesome</span>
                <span>AI Schrijven</span>
              </div>
              <div className="lp2-about-feat">
                <span className="material-icons">mic</span>
                <span>Spraak input</span>
              </div>
              <div className="lp2-about-feat">
                <span className="material-icons">description</span>
                <span>PDF Export</span>
              </div>
              <div className="lp2-about-feat">
                <span className="material-icons">speed</span>
                <span>10 min klaar</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ═══════ 8. MARQUEE ═══════ */}
      <ScreenshotMarquee />

      {/* ═══════ 9. PRICING ═══════ */}
      <section id="prijzen" className="lp2-section lp2-section--center">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-10%' }}
          variants={revealParent}
        >
          <motion.div variants={revealItem}>
            <span className="lp2-section-label">
              <span className="material-icons" style={{ fontSize: 14 }}>payments</span>
              Abonnementen
            </span>
            <h2 className="lp2-section-title">Kies je abonnement</h2>
            <p className="lp2-section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
              Start met een gratis proefperiode van 7 dagen. Geen creditcard nodig.
            </p>
          </motion.div>

          <div className="lp2-plans">
            <motion.article
              className={`lp2-plan ${plan === 'personal' ? 'is-selected' : ''}`}
              variants={revealItem}
              whileHover={{ y: -4 }}
            >
              <span className="lp2-plan-popular">Meest gekozen</span>
              <div className="lp2-plan-head">
                <h3>Personal</h3>
                <span className="lp2-plan-price">&euro; 49 / maand</span>
              </div>
              <ul className="lp2-plan-bullets">
                <li>1 gebruiker</li>
                <li>Volledige chat + tools</li>
                <li>Spraak-naar-tekst</li>
                <li>PDF export + tekst verbeteren</li>
                <li>7 dagen gratis proberen</li>
              </ul>
              <div className="lp2-plan-cta">
                <button
                  className="lp2-btn-primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => {
                    setPlan('personal');
                    document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Start gratis proefperiode
                </button>
              </div>
            </motion.article>

            <motion.article
              className={`lp2-plan ${plan === 'enterprise' ? 'is-selected' : ''}`}
              variants={revealItem}
              whileHover={{ y: -4 }}
            >
              <div className="lp2-plan-head">
                <h3>Enterprise</h3>
                <span className="lp2-plan-price">Maatwerk offerte</span>
              </div>
              <ul className="lp2-plan-bullets">
                <li>Meerdere seats</li>
                <li>Beveiliging &amp; support</li>
                <li>Integraties / branding</li>
                <li>Dedicated account manager</li>
                <li>Aangepaste AI-configuratie</li>
              </ul>
              <div className="lp2-plan-cta">
                <button
                  className="lp2-btn-ghost"
                  style={{ width: '100%', justifyContent: 'center' }}
                  onClick={() => {
                    setPlan('enterprise');
                    document.getElementById('signup')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Neem contact op
                </button>
              </div>
            </motion.article>
          </div>
        </motion.div>
      </section>

      {/* ═══════ 10. CTA FORM ═══════ */}
      <section id="signup" className="lp2-section">
        <motion.div
          className="lp2-cta-surface"
          initial={floatUp.hidden}
          whileInView={floatUp.show}
          viewport={{ once: true }}
        >
          <div className="lp2-cta-head">
            <h2>Start je gratis proefperiode</h2>
            <p>Probeer HAI Job 7 dagen gratis. Daarna &euro;49/maand voor het Personal plan. Elk moment opzegbaar.</p>
          </div>

          <form
            className="lp2-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (plan === 'enterprise') {
                window.location.href = mailtoHref;
                return;
              }
              handleAnonymousCheckout(e);
            }}
          >
            <div className="lp2-form-grid">
              <label className="lp2-field">
                <span>Naam</span>
                <input
                  type="text"
                  placeholder="Voor- en achternaam"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </label>
              <label className="lp2-field">
                <span>E-mail</span>
                <input
                  type="email"
                  placeholder="jij@bedrijf.nl"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </label>
              <label className="lp2-field">
                <span>Telefoon (optioneel)</span>
                <input
                  type="tel"
                  placeholder="06-..."
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </label>
            </div>

            <fieldset className="lp2-field" style={{ border: 'none', padding: 0, margin: 0 }}>
              <span>Abonnement</span>
              <div className="lp2-segmented" role="tablist" aria-label="Abonnement">
                <button
                  type="button"
                  role="tab"
                  aria-selected={plan === 'personal'}
                  className={`lp2-seg-item ${plan === 'personal' ? 'is-active' : ''}`}
                  onClick={() => setPlan('personal')}
                >
                  <span className="lp2-seg-title">Personal</span>
                  <span className="lp2-seg-sub">&euro;49/mnd</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={plan === 'enterprise'}
                  className={`lp2-seg-item ${plan === 'enterprise' ? 'is-active' : ''}`}
                  onClick={() => setPlan('enterprise')}
                >
                  <span className="lp2-seg-title">Enterprise</span>
                  <span className="lp2-seg-sub">maatwerk</span>
                </button>
              </div>
              <input type="hidden" name="plan" value={plan} />
            </fieldset>

            <div className="lp2-form-actions">
              <button className="lp2-btn-primary" type="submit" disabled={checkoutLoading}>
                {checkoutLoading
                  ? 'Laden...'
                  : plan === 'enterprise'
                    ? 'Offerte Aanvragen'
                    : 'Start 7 Dagen Gratis'}
              </button>
            </div>

            {checkoutError && (
              <div className="lp2-form-error">{checkoutError}</div>
            )}

            <small className="lp2-privacy-note">
              {plan === 'enterprise'
                ? 'We nemen contact met je op voor een persoonlijk gesprek over de Enterprise mogelijkheden.'
                : 'Start je gratis proefperiode van 7 dagen. Geen creditcard vereist vooraf.'}
            </small>
          </form>
        </motion.div>
      </section>

      {/* ═══════ 11. FAQ ═══════ */}
      <div className="lp2-section-full lp2-section-full--alt">
        <section className="lp2-section lp2-section--center" style={{ padding: '80px 24px' }}>
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={revealParent}
          >
            <motion.div variants={revealItem}>
              <h2 className="lp2-section-title">Veelgestelde vragen</h2>
              <p className="lp2-section-sub" style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                Alles wat je wilt weten over HAI Job.
              </p>
            </motion.div>
            <div className="lp2-faq-grid">
              <FAQCard
                q="Is er een gratis proefperiode?"
                a="Ja, je kunt HAI Job 7 dagen gratis proberen. Daarna betaal je \u20AC49 per maand voor het Personal plan. Je kunt op elk moment opzeggen."
              />
              <FAQCard
                q="Is mijn data veilig?"
                a="Absoluut. Je gesprekken worden na je sessie verwijderd en nooit gebruikt om AI-modellen te trainen. Alle communicatie is versleuteld."
              />
              <FAQCard
                q="Werkt het op mobiel?"
                a="Ja, HAI Job werkt uitstekend op mobiel. De spraak-naar-tekst functie maakt het extra handig voor onderweg."
              />
            </div>
          </motion.div>
        </section>
      </div>

      {/* ═══════ 12. FOOTER ═══════ */}
      <footer className="lp2-footer">
        <div className="lp2-footer-links">
          <button onClick={() => setShowPrivacyModal(true)}>Privacy</button>
          <span className="lp2-footer-dot">&bull;</span>
          <button onClick={() => setShowTermsModal(true)}>Voorwaarden</button>
        </div>
        <div className="lp2-footer-links">
          <span>&copy; {new Date().getFullYear()} HumanAIze</span>
          <span className="lp2-footer-dot">&bull;</span>
          <a href="https://humanaize.nl/" target="_blank" rel="noreferrer">humanaize.nl</a>
        </div>
      </footer>

      {/* ═══════ MODALS ═══════ */}
      <PrivacyModal isVisible={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
      <TermsModal isVisible={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </main>
  );
}
